import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/Button';
import {
  JobSearchModal as SharedJobSearchModal,
  type JobSearchResult,
} from '@/components/JobSearchModal';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { JobSearchResult };

export interface BillableItem {
  id: string;
  source: 'wip' | 'time_entry' | 'service';
  description: string;
  hours: number;
  rate: number;
  amount: number;
  date: string | null;
}

export interface SelectedJobWithItems {
  job: JobSearchResult;
  items: BillableItem[];
  totalAmount: number;
}

interface JobSearchModalProps {
  onSelect: (result: SelectedJobWithItems) => void;
  onClose: () => void;
  /** Pre-filter jobs by this client ID if provided */
  clientId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Billing-specific job search modal.
 *
 * Two-step flow:
 * 1. Search & select a job (uses the shared JobSearchModal)
 * 2. Preview billable items for the selected job before confirming
 *
 * Used by InvoiceForm and BillingTransactionForm.
 */
export function JobSearchModal({ onSelect, onClose, clientId }: JobSearchModalProps) {
  const [selectedJob, setSelectedJob] = useState<JobSearchResult | null>(null);

  // ─── Billable items query (fires when a job is selected for preview) ────────
  const { data: billableData, isLoading: billableLoading } = useQuery({
    queryKey: ['billable-items', selectedJob?.id],
    queryFn: async () => {
      const res = await apiClient.get<{
        success: boolean;
        data: {
          job: JobSearchResult;
          items: BillableItem[];
          totalAmount: number;
        };
      }>(`/jobs/${selectedJob!.id}/billable-items`);
      return res.data.data;
    },
    enabled: !!selectedJob,
    staleTime: 30_000,
  });

  const handleJobSelected = useCallback((job: JobSearchResult) => {
    setSelectedJob(job);
  }, []);

  const handleConfirmSelect = useCallback(() => {
    if (!billableData || !selectedJob) return;
    onSelect({
      job: selectedJob,
      items: billableData.items,
      totalAmount: billableData.totalAmount,
    });
  }, [billableData, selectedJob, onSelect]);

  const handleBack = useCallback(() => {
    setSelectedJob(null);
  }, []);

  // ─── Step 1: Job search (shared modal) ─────────────────────────────────────
  if (!selectedJob) {
    return (
      <SharedJobSearchModal
        onSelect={handleJobSelected}
        onClose={onClose}
        clientId={clientId}
        title="Search & Select Job"
        defaultStatus="in_progress"
      />
    );
  }

  // ─── Step 2: Billable items preview ────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-search-modal-title"
    >
      <div className="flex h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label="Back to search"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <h2
              id="job-search-modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Billable Items Preview
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {billableLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                <span className="ml-2 text-sm text-gray-500">Loading billable items...</span>
              </div>
            ) : billableData ? (
              <div className="space-y-4">
                {/* Job summary */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {billableData.job.title}
                    </span>
                    {billableData.job.jobCode && (
                      <span className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        {billableData.job.jobCode}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {billableData.job.jobType} · {billableData.items.length} billable item
                    {billableData.items.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {billableData.items.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      No unbilled items found for this job. You can still select it and add line
                      items manually.
                    </p>
                  </div>
                ) : (
                  (() => {
                    const hasHours = billableData.items.some((i) => i.hours > 0);
                    return (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
                            <th className="pb-2 pr-4">Description</th>
                            {hasHours && <th className="pb-2 pr-4 text-right">Hours</th>}
                            <th className="pb-2 pr-4 text-right">Amount</th>
                            {hasHours && <th className="pb-2 text-right">Total</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {billableData.items.map((item) => (
                            <tr key={item.id} className="py-2">
                              <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">
                                {item.description}
                                <span className="ml-1.5 text-xs text-gray-400">
                                  {item.source === 'service'
                                    ? '(Service)'
                                    : item.source === 'wip'
                                      ? '(WIP)'
                                      : '(Time)'}
                                </span>
                              </td>
                              {hasHours && (
                                <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">
                                  {item.hours > 0 ? `${item.hours}h` : '—'}
                                </td>
                              )}
                              <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">
                                {item.amount.toLocaleString('en', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              {hasHours && (
                                <td className="py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                                  {(item.hours > 0
                                    ? item.hours * item.amount
                                    : item.amount
                                  ).toLocaleString('en', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                            <td
                              colSpan={hasHours ? 3 : 1}
                              className="pt-2 text-right text-sm font-semibold text-gray-700 dark:text-gray-300"
                            >
                              Total
                            </td>
                            <td className="pt-2 text-right text-sm font-bold text-gray-900 dark:text-gray-100">
                              {billableData.items
                                .reduce(
                                  (sum, i) => sum + (i.hours > 0 ? i.hours * i.amount : i.amount),
                                  0,
                                )
                                .toLocaleString('en', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    );
                  })()
                )}
              </div>
            ) : null}
          </div>

          {/* Confirm footer */}
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <p className="text-xs text-gray-500">
              {billableData && billableData.items.length > 0
                ? 'These items will be added as invoice line items.'
                : 'Job will be linked with no pre-filled line items.'}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" type="button" onClick={handleBack}>
                Back
              </Button>
              <Button
                type="button"
                onClick={handleConfirmSelect}
                disabled={billableLoading}
                loading={billableLoading}
              >
                Add to Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
